import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle, Clock, Copy, MessageCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
const fecha = (iso) => (iso ? new Date(iso).toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' }) : '');

const copiarTexto = async (texto) => {
  try {
    await navigator.clipboard.writeText(texto);
  } catch (e) {
    const area = Object.assign(document.createElement('textarea'), { value: texto });
    document.body.appendChild(area);
    area.select();
    document.execCommand('copy');
    area.remove();
  }
};

const EstadoEnvio = ({ ultimo, total }) => (ultimo ? (
  <p className="flex items-center gap-2 text-sm text-emerald-400" data-testid="bienvenida-estado">
    <CheckCircle className="h-4 w-4 shrink-0" />
    Bienvenida {ultimo.canal === 'copiado' ? 'copiada' : 'enviada'} el {fecha(ultimo.fecha)}{ultimo.enviado_por_nombre ? ` por ${ultimo.enviado_por_nombre}` : ''}{total > 1 ? ` · ${total} envíos` : ''}
  </p>
) : (
  <p className="flex items-center gap-2 text-sm text-amber-400" data-testid="bienvenida-estado">
    <Clock className="h-4 w-4 shrink-0" />Bienvenida pendiente de enviar
  </p>
));

export default function BienvenidaMiJPM({ clienteId }) {
  const [datos, setDatos] = useState(null);

  const cargar = useCallback(() => {
    axios.get(`${API_URL}/api/mi-jpm/cliente/${clienteId}/mensaje-whatsapp`, auth())
      .then(r => setDatos(r.data))
      .catch(e => toast.error(e.response?.data?.detail || 'No se pudo preparar el mensaje de bienvenida'));
  }, [clienteId]);
  useEffect(() => { cargar(); }, [cargar]);

  if (!datos) return <div className="h-10 animate-pulse rounded-lg bg-zinc-800" data-testid="bienvenida-cargando" />;

  const yaEnviada = !!datos.ultimo_envio;
  const confirmar = () => !yaEnviada || window.confirm(
    `La bienvenida ya se compartió el ${fecha(datos.ultimo_envio.fecha)}. ¿Quieres enviarla de nuevo a este cliente?`);
  const registrar = (canal) => axios.post(`${API_URL}/api/mi-jpm/cliente/${clienteId}/registrar-envio-acceso`, { canal, reenviar: yaEnviada }, auth())
    .then(cargar).catch(e => toast.error(e.response?.data?.detail || 'No se pudo registrar el envío'));

  const abrirWhatsApp = () => {
    if (!confirmar()) return;
    window.open(datos.whatsapp_url, '_blank', 'noopener');
    registrar('whatsapp');
    toast.success('WhatsApp abierto con el mensaje de bienvenida');
  };
  const copiar = async () => {
    if (!confirmar()) return;
    await copiarTexto(datos.mensaje);
    registrar('copiado');
    toast.success('Mensaje de bienvenida copiado');
  };

  return (
    <div className="space-y-3" data-testid="bienvenida-mi-jpm">
      <EstadoEnvio ultimo={datos.ultimo_envio} total={datos.total_envios} />
      <pre className="max-h-72 overflow-y-auto whitespace-pre-wrap break-words rounded-lg border border-zinc-800 bg-zinc-950 p-3 font-sans text-sm leading-relaxed text-zinc-300" data-testid="bienvenida-mensaje">{datos.mensaje}</pre>
      {!datos.telefono_whatsapp && <p className="text-xs text-zinc-500" data-testid="bienvenida-sin-celular">El cliente no tiene un celular válido: WhatsApp se abrirá para que elijas el contacto.</p>}
      <div className="flex flex-col gap-2 sm:flex-row">
        <button type="button" onClick={abrirWhatsApp} className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-emerald-600 px-4 py-2.5 font-medium text-white transition-colors hover:bg-emerald-500" data-testid="bienvenida-whatsapp">
          <MessageCircle className="h-4 w-4" />{yaEnviada ? 'Reenviar por WhatsApp' : 'Enviar por WhatsApp'}
        </button>
        <button type="button" onClick={copiar} className="flex flex-1 items-center justify-center gap-2 rounded-lg border border-zinc-700 px-4 py-2.5 font-medium text-zinc-200 transition-colors hover:bg-zinc-800" data-testid="bienvenida-copiar">
          <Copy className="h-4 w-4" />Copiar mensaje
        </button>
      </div>
    </div>
  );
}
